import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PhotoPickerModal } from '@/components/ui/PhotoPickerModal'
import { Link, router, useForm } from '@inertiajs/react'
import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useConfirm } from '@/hooks/useConfirm'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { SUNLIGHT_ICONS, DIFFICULTY_ICONS, CARE_ACTION_ICONS, EnumIcon, PLACEHOLDER_ICONS } from '@/lib/icons'
import { IconSelect } from '@/components/ui/IconSelect'

// --- Types ---

interface CareTask {
  id: number
  task_type: string
  task_type_pl: string
  frequency_days: number | null
  instructions: string | null
  is_active: boolean
  next_due_at: string | null
  last_done_at: string | null
  overdue: boolean
  due_today: boolean
}

interface CareLog {
  id: number
  action: string
  action_pl: string
  notes: string | null
  performed_at: string
}

interface Plant {
  id: number
  name: string
  species_name: string | null
  status: string | null
  status_pl: string | null
  watering_frequency_days: number | null
  watering_amount_ml: number | null
  last_watered_at: string | null
  sunlight_preference: string | null
  difficulty_level: string | null
  acquired_at: string | null
  notes: string | null
  days_until_watering: number | null
  needs_watering: boolean
  cover_photo_url: string | null
  thumbnail_url: string | null
  species_illustration_url: string | null
  pot_diameter_cm: number | null
  location: { id: number; name: string } | null
  care_tasks: CareTask[]
  care_logs: CareLog[]
}

interface Props {
  plant: Plant
}

// --- Helpers ---

const sunlightLabels: Record<string, string> = {
  full_sun: 'Jasne bezpośrednie', indirect: 'Jasne rozproszone', partial_shade: 'Półcień', shade: 'Cień',
}
const difficultyLabels: Record<string, string> = {
  easy: 'Łatwa', medium_difficulty: 'Średnia', hard: 'Trudna',
}
const careActionOptions = [
  { value: 'watering', label: 'Podlewanie' },
  { value: 'fertilizing', label: 'Nawożenie' },
  { value: 'pruning', label: 'Przycinanie' },
  { value: 'repotting', label: 'Przesadzanie' },
  { value: 'misting', label: 'Zraszanie' },
  { value: 'rotating', label: 'Obracanie' },
  { value: 'cleaning', label: 'Czyszczenie' },
  { value: 'pest_check', label: 'Kontrola szkodników' },
]

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

function groupLogsByMonth(logs: CareLog[]): { label: string; logs: CareLog[] }[] {
  const groups = new Map<string, { label: string; logs: CareLog[] }>()
  for (const log of logs) {
    const d = new Date(log.performed_at)
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`
    if (!groups.has(key)) {
      const label = d.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
      groups.set(key, { label, logs: [] })
    }
    groups.get(key)!.logs.push(log)
  }
  return Array.from(groups.values())
}

// --- Main Component ---

type TabId = 'care' | 'journal' | 'settings'

function PlantsShow({ plant }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('care')
  const [showLogModal, setShowLogModal] = useState(false)
  const [showPhotoPicker, setShowPhotoPicker] = useState(false)
  const { pending, confirm, handleConfirm, handleCancel } = useConfirm()

  const handleWater = useCallback(() => {
    router.post(`/plants/${plant.id}/water`)
  }, [plant.id])

  const handleComplete = useCallback((taskId: number) => {
    router.post(`/care_tasks/${taskId}/complete`)
  }, [])

  const handleSelectIllustration = useCallback((speciesId: number) => {
    router.post(`/plants/${plant.id}/set_species_photo`, { species_id: speciesId })
  }, [plant.id])

  const handleUploadPhoto = useCallback((file: File) => {
    const formData = new FormData()
    formData.append('photo', file)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    fetch(`/plants/${plant.id}/add_photo`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrf || '' },
      body: formData,
    }).then((res) => {
      if (!res.ok) throw new Error('Upload failed')
      router.reload()
    }).catch(() => alert('Nie udało się dodać zdjęcia. Spróbuj ponownie.'))
  }, [plant.id])

  const handleDelete = useCallback(() => {
    confirm('Na pewno usunąć tę roślinę?', () => router.delete(`/plants/${plant.id}`))
  }, [plant.id, confirm])

  const activeTasks = plant.care_tasks.filter(t => t.is_active)
  const recentLogs = plant.care_logs.slice(0, 15)

  return (
    <AtlasPage>
      {/* === BOOK SPREAD === */}
      <div className="flex flex-col sm:flex-row gap-sm sm:gap-md">
        {/* Left page: Photo + Identity */}
        <div className="sm:w-[35%] shrink-0 text-center">
          {/* Cover photo — single primary image */}
          {plant.cover_photo_url ? (
            <div className="mb-sm">
              <div className="aspect-square w-full overflow-hidden border border-frame/20 mx-auto">
                <img src={plant.cover_photo_url} alt={plant.name} className="h-full w-full object-contain" />
              </div>
              <button
                type="button"
                onClick={() => setShowPhotoPicker(true)}
                className="mt-xs text-[0.7rem] text-frame hover:text-botanical cursor-pointer bg-transparent border-none underline decoration-frame/30 hover:decoration-botanical transition-colors"
              >
                Zmień zdjęcie
              </button>
            </div>
          ) : plant.species_illustration_url ? (
            <div className="mb-sm">
              <div className="aspect-square w-full overflow-hidden border border-frame/20 mx-auto">
                <img src={plant.species_illustration_url} alt={plant.name} className="h-full w-full object-contain" />
              </div>
              <button
                type="button"
                onClick={() => setShowPhotoPicker(true)}
                className="mt-xs text-[0.7rem] text-botanical cursor-pointer bg-transparent border-none underline decoration-botanical/30 hover:decoration-botanical transition-colors"
              >
                Dodaj własne zdjęcie
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowPhotoPicker(true)}
              className="aspect-square w-full flex flex-col items-center justify-center mb-sm cursor-pointer transition-colors bg-transparent group"
              style={{ background: 'var(--paper-dark)', border: '1px dashed var(--desk)' }}
            >
              <PLACEHOLDER_ICONS.camera.icon weight="regular" size={28} className="text-frame mb-xs group-hover:text-botanical transition-colors" />
              <span className="text-frame text-sm italic group-hover:text-botanical transition-colors">Dodaj zdjęcie</span>
            </button>
          )}

          <h1 className="font-display text-lg tracking-[0.12em] uppercase text-ink leading-tight">
            {plant.name}
          </h1>
          {plant.species_name && (
            <p className="font-display italic text-sm text-frame mt-[2px]">{plant.species_name}</p>
          )}

          <div className="flex items-center justify-center gap-xs mt-xs">
            <StatusBadge status={plant.status} />
          </div>

          {plant.needs_watering && (
            <div className="mt-sm">
              <Button onClick={handleWater} size="sm">
                Podlej teraz
              </Button>
            </div>
          )}
        </div>

        {/* Vertical divider (desktop only) */}
        <div className="hidden sm:block w-px self-stretch" style={{ background: 'var(--desk)' }} />

        {/* Right page: Dane rośliny */}
        <div className="flex-1 min-w-0">
          <div className="font-label text-[0.6rem] uppercase tracking-[0.15em] text-frame mb-sm font-semibold">
            Dane rośliny
          </div>
          <div className="text-[0.85rem] leading-[1.8]">
            <SpecimenRow label="miejsce">
              {plant.location ? (
                <Link href={`/locations/${plant.location.id}`} className="text-botanical underline decoration-botanical/30 hover:decoration-botanical">
                  {plant.location.name}
                </Link>
              ) : '—'}
            </SpecimenRow>
            <SpecimenRow label="doniczka">
              {plant.pot_diameter_cm ? `${plant.pot_diameter_cm} cm` : '—'}
            </SpecimenRow>
            <SpecimenRow label="podlewanie">
              {plant.watering_frequency_days
                ? `Co ${plant.watering_frequency_days} dni, ${plant.watering_amount_ml || '?'} ml`
                : '—'}
            </SpecimenRow>
            <SpecimenRow label="do podlania">
              {plant.days_until_watering !== null ? (
                <span className={plant.needs_watering ? 'text-danger font-semibold' : ''}>
                  {plant.needs_watering ? 'Teraz!' : `${plant.days_until_watering} dni`}
                </span>
              ) : '—'}
            </SpecimenRow>
            <SpecimenRow label="nasłonecznienie">
              {plant.sunlight_preference ? (
                <span className="inline-flex items-center gap-xs">
                  <EnumIcon entry={SUNLIGHT_ICONS[plant.sunlight_preference]} size={14} />
                  {sunlightLabels[plant.sunlight_preference] || plant.sunlight_preference}
                </span>
              ) : '—'}
            </SpecimenRow>
            <SpecimenRow label="trudność">
              {plant.difficulty_level ? (
                <span className="inline-flex items-center gap-xs">
                  <EnumIcon entry={DIFFICULTY_ICONS[plant.difficulty_level]} size={14} />
                  {difficultyLabels[plant.difficulty_level] || plant.difficulty_level}
                </span>
              ) : '—'}
            </SpecimenRow>
            <SpecimenRow label="od">
              {formatDate(plant.acquired_at)}
            </SpecimenRow>
          </div>

          {plant.notes && (
            <div className="mt-sm pt-sm" style={{ borderTop: '1px solid var(--desk)' }}>
              <span className="text-frame text-[0.75rem]" style={{ fontVariant: 'small-caps' }}>
                notatki:
              </span>{' '}
              <span className="text-[0.85rem] italic text-ink-light">{plant.notes}</span>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* === TAB BAR === */}
      <div className="flex" style={{ borderBottom: '2px solid var(--frame)' }}>
        {(['care', 'journal', 'settings'] as const).map(tab => {
          const labels: Record<TabId, string> = { care: 'Pielęgnacja', journal: 'Dziennik', settings: 'Ustawienia' }
          const isActive = activeTab === tab
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 text-center py-sm font-label text-[0.7rem] uppercase tracking-[0.12em] transition-colors cursor-pointer border-none ${
                isActive
                  ? 'text-ink font-semibold'
                  : 'text-frame hover:text-ink-light'
              }`}
              style={{
                background: isActive ? 'var(--paper)' : 'var(--paper-dark)',
                borderBottom: isActive ? '2px solid var(--botanical)' : '2px solid transparent',
                marginBottom: '-2px',
              }}
            >
              {labels[tab]}
            </button>
          )
        })}
      </div>

      {/* === TAB CONTENT === */}
      <div className="pt-sm animate-[fade-in_0.15s_ease-out]" key={activeTab}>
        {activeTab === 'care' && (
          <CareTab
            tasks={activeTasks}
            plantId={plant.id}
            onComplete={handleComplete}
            onOpenLog={() => setShowLogModal(true)}
          />
        )}
        {activeTab === 'journal' && (
          <JournalTab logs={recentLogs} plantId={plant.id} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab plant={plant} onDelete={handleDelete} />
        )}
      </div>

      <PageNumber number={3} />

      {/* === LOG CARE MODAL === */}
      {showLogModal && (
        <LogCareModal
          plantId={plant.id}
          onClose={() => setShowLogModal(false)}
        />
      )}

      {/* === PHOTO PICKER MODAL === */}
      <PhotoPickerModal
        isOpen={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelectIllustration={handleSelectIllustration}
        onUploadPhoto={handleUploadPhoto}
        currentSpeciesName={plant.species_name}
      />
      {pending && <ConfirmModal message={pending.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
    </AtlasPage>
  )
}

// --- Sub-components ---

function SpecimenRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-sm text-[0.85rem]">
      <span className="text-frame min-w-[7rem] shrink-0 text-[0.8rem]" style={{ fontVariant: 'small-caps' }}>
        {label}:
      </span>
      <span className="text-ink">{children}</span>
    </div>
  )
}

function CareTab({ tasks, plantId, onComplete, onOpenLog }: {
  tasks: CareTask[]
  plantId: number
  onComplete: (id: number) => void
  onOpenLog: () => void
}) {
  return (
    <>
      {tasks.length === 0 ? (
        <p className="text-sm text-frame italic text-center py-md">Brak aktywnych zadań pielęgnacyjnych.</p>
      ) : (
        <div className="space-y-xs">
          {tasks.map(task => (
            <div
              key={task.id}
              className="flex items-start justify-between gap-sm py-xs px-sm rounded-r"
              style={{
                borderLeft: `3px solid ${task.overdue ? 'var(--danger)' : 'var(--botanical)'}`,
                background: task.overdue ? 'rgba(139,58,58,0.04)' : 'transparent',
              }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[0.85rem] flex items-center flex-wrap">
                  <EnumIcon entry={CARE_ACTION_ICONS[task.task_type]} size={14} className={task.overdue ? 'text-danger' : CARE_ACTION_ICONS[task.task_type]?.color} />
                  <span className={`font-semibold ml-xs ${task.overdue ? 'text-danger' : 'text-ink'}`}>
                    {task.task_type_pl}
                  </span>
                  {task.frequency_days && (
                    <span className="text-frame text-xs ml-sm">co {task.frequency_days} dni</span>
                  )}
                  {task.overdue && (
                    <span className="text-danger text-xs ml-sm italic">zaległe!</span>
                  )}
                </p>
                {task.next_due_at && (
                  <p className="text-xs text-frame mt-xs">
                    {task.overdue ? 'Zaległe od: ' : 'Następne: '}
                    {formatDate(task.next_due_at)}
                  </p>
                )}
                {task.instructions && (
                  <p className="text-xs text-ink-light italic mt-xs">{task.instructions}</p>
                )}
              </div>
              <Button size="sm" onClick={() => onComplete(task.id)}>Wykonaj</Button>
            </div>
          ))}
        </div>
      )}

      {/* Quick action bar */}
      <div className="flex gap-sm mt-md pt-sm" style={{ borderTop: '1px solid var(--desk)' }}>
        <button
          onClick={onOpenLog}
          className="border border-botanical text-botanical px-md py-xs text-xs font-label tracking-wide cursor-pointer bg-transparent hover:bg-botanical-light transition-colors"
        >
          + Zaloguj czynność
        </button>
        <Button href={`/plants/${plantId}/care_tasks/new`} variant="secondary" size="sm">
          + Dodaj zadanie
        </Button>
      </div>
    </>
  )
}

function JournalTab({ logs, plantId }: { logs: CareLog[]; plantId: number }) {
  if (logs.length === 0) {
    return <p className="text-sm text-frame italic text-center py-md">Brak wpisów w dzienniku.</p>
  }

  const groups = groupLogsByMonth(logs)

  return (
    <>
      {groups.map(group => (
        <div key={group.label} className="mb-md">
          <div className="font-label text-[0.6rem] uppercase tracking-[0.12em] text-gold font-semibold mb-xs">
            {group.label}
          </div>
          <div>
            {group.logs.map(log => (
              <div key={log.id} className="flex gap-sm py-xs" style={{ borderBottom: '1px solid var(--paper-dark)' }}>
                <div className="w-[3rem] text-right text-frame text-[0.7rem] shrink-0" style={{ fontVariant: 'small-caps' }}>
                  {formatShortDate(log.performed_at)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[0.85rem] font-medium text-ink inline-flex items-center gap-xs">
                    <EnumIcon entry={CARE_ACTION_ICONS[log.action]} size={13} />
                    {log.action_pl}
                  </span>
                  {log.notes && (
                    <span className="text-[0.75rem] text-ink-light italic ml-xs">{log.notes}</span>
                  )}
                </div>
                <div className="text-[0.7rem] text-frame whitespace-nowrap shrink-0">
                  {formatTime(log.performed_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-center mt-sm">
        <Button href={`/plants/${plantId}/care_logs`} variant="secondary" size="sm">
          Wszystkie wpisy
        </Button>
      </div>
    </>
  )
}

function SettingsTab({ plant, onDelete }: { plant: Plant; onDelete: () => void }) {
  return (
    <>
      <div className="space-y-sm">
        <SettingsLink
          href={`/plants/${plant.id}/edit`}
          title="Edytuj roślinę"
          description="Zmień nazwę, zdjęcia, dane, lokalizację"
        />
        <SettingsLink
          href={`/plants/${plant.id}/care_tasks`}
          title="Zarządzaj zadaniami"
          description="Dodaj, edytuj lub dezaktywuj zadania pielęgnacyjne"
        />
        {plant.location && (
          <SettingsLink
            href={`/locations/${plant.location.id}`}
            title="Miejsce"
            description={plant.location.name}
          />
        )}
      </div>

      {/* Danger zone */}
      <div className="mt-lg pt-md" style={{ borderTop: '1px solid var(--desk)' }}>
        <div className="font-label text-[0.6rem] uppercase tracking-[0.12em] text-danger font-semibold mb-sm">
          Strefa niebezpieczna
        </div>
        <div
          className="flex items-center justify-between p-sm cursor-pointer"
          style={{ border: '1px solid var(--danger)' }}
        >
          <div>
            <p className="text-sm font-medium text-danger">Usuń roślinę</p>
            <p className="text-xs text-frame">Trwałe usunięcie rośliny i całej jej historii</p>
          </div>
          <Button variant="danger" size="sm" onClick={onDelete}>Usuń</Button>
        </div>
      </div>
    </>
  )
}

function SettingsLink({ href, title, description }: { href: string; title: string; description: string }) {
  return (
    <Link
      href={href}
      className="flex items-center justify-between p-sm transition-colors block"
      style={{ border: '1px solid var(--desk)', background: 'var(--paper-dark)' }}
    >
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="text-xs text-frame">{description}</p>
      </div>
      <span className="text-frame text-lg">&rsaquo;</span>
    </Link>
  )
}

function LogCareModal({ plantId, onClose }: { plantId: number; onClose: () => void }) {
  const form = useForm({ care_action: 'watering', notes: '' })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`/plants/${plantId}/log_care`, {
      onSuccess: () => onClose(),
    })
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-md"
      style={{ background: 'rgba(58,42,24,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div
        className="w-full max-w-sm p-lg animate-[modal-in_0.2s_ease-out] rounded"
        style={{ background: 'var(--paper)', border: '2px double var(--frame)' }}
      >
        <div className="flex items-center justify-between mb-md">
          <h3 className="font-display text-base uppercase tracking-wider text-ink">Zaloguj czynność</h3>
          <button onClick={onClose} className="text-frame text-lg hover:text-ink cursor-pointer bg-transparent border-none">
            &times;
          </button>
        </div>

        <Separator />

        <form onSubmit={handleSubmit} className="space-y-md mt-md">
          <div>
            <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
              Czynność
            </label>
            <IconSelect
              value={form.data.care_action}
              onChange={(v) => form.setData('care_action', v)}
              options={careActionOptions}
              iconMap={CARE_ACTION_ICONS}
              placeholder=""
            />
          </div>

          <div>
            <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
              Notatki
            </label>
            <input
              type="text"
              value={form.data.notes}
              onChange={(e) => form.setData('notes', e.target.value)}
              placeholder="Opcjonalne..."
              className="form-input"
            />
          </div>

          <div className="flex justify-end gap-sm">
            <button
              type="button"
              onClick={onClose}
              className="px-md py-xs text-sm font-label text-frame border border-frame bg-transparent cursor-pointer hover:bg-paper-dark transition-colors"
            >
              Anuluj
            </button>
            <Button type="submit" disabled={form.processing} size="sm">
              Zapisz
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

// --- Layout & Export ---

PlantsShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default PlantsShow
